import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import ProductList from '../components/ProductList';
import { fetchCategories, buildCategoryTree } from '../lib/categories';
import { fetchSuppliersPublic } from '../lib/suppliers';
import { getFileUrl } from '../lib/pb';

export default function CatalogueCategory() {
  const { slug } = useParams();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [familyRoot, setFamilyRoot] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedFilterIds, setSelectedFilterIds] = useState(() => {
    try {
      const raw = localStorage.getItem(`catalogueCategory.${slug}.selectedFilterIds`);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [expandedIds, setExpandedIds] = useState([]);
  const [productsCount, setProductsCount] = useState(0);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedRootId, setSelectedRootId] = useState(() => localStorage.getItem(`catalogueCategory.${slug}.selectedRootId`) || '');
  const [sortMode, setSortMode] = useState(() => localStorage.getItem(`catalogueCategory.${slug}.sortMode`) || 'random');
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const productsTopRef = useRef(null);
  const skipScrollRef = useRef(true);
  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      try {
        const cats = await fetchCategories('catalogue');
        if (cancelled) return;
        setCategories(cats || []);
        const tree = buildCategoryTree(cats);

        const findBySlug = (nodes, slugValue) => {
          for (const n of nodes) {
            if (n.slug === slugValue) return n;
            const child = findBySlug(n.children || [], slugValue);
            if (child) return child;
          }
          return null;
        };

        const match = findBySlug(tree, slug);
        if (match) {
          // Always use the matched slug node as the current category
          const rootNode = match;
          setSelected(rootNode);
          setFamilyRoot(rootNode);

          let ids = [];
          try {
            const raw = localStorage.getItem(`catalogueCategory.${slug}.selectedFilterIds`);
            const savedIds = raw ? JSON.parse(raw) : [];
            if (Array.isArray(savedIds) && savedIds.length) {
              ids = savedIds;
            }
          } catch { }
          if (!ids.length) {
            ids = [rootNode.id, ...collectDescendantIds(rootNode)];
          }
          setSelectedFilterIds(ids);
          // replier par défaut; on dépliera au besoin
          setExpandedIds([]);
          setSelectedRootId(rootNode.id);

          // sous-catégories à plat (tous niveaux) pour la grille
          const flatSubs = [];
          const stack = [...(rootNode.children || [])];
          while (stack.length) {
            const current = stack.pop();
            if (!current) continue;
            flatSubs.push(current);
            if (current.children && current.children.length) {
              stack.push(...current.children);
            }
          }
          setSubCategories(flatSubs);
        } else if (slug && !cancelled) {
          // Si le slug n'est pas une catégorie, c'est probablement un produit.
          // On redirige vers la route produit standard pour lever l'ambiguïté.
          navigate(`/produits/${encodeURIComponent(slug)}`, { replace: true });
        }
      } catch (e) {
        // on ignore discrètement l'erreur côté page catégorie
      }
    }

    // Clear previous state tied to the previous slug to avoid stale selections
    setSelected(null);
    setFamilyRoot(null);
    setSubCategories([]);
    setSelectedFilterIds([]);
    setExpandedIds([]);
    loadCategories();
    const t = setTimeout(() => { skipScrollRef.current = false; }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [slug]);

  // Persist to localStorage on change
  useEffect(() => {
    try { localStorage.setItem(`catalogueCategory.${slug}.sortMode`, sortMode); } catch { }
  }, [slug, sortMode]);
  useEffect(() => {
    try { localStorage.setItem(`catalogueCategory.${slug}.selectedRootId`, selectedRootId || ''); } catch { }
  }, [slug, selectedRootId]);
  useEffect(() => {
    try { localStorage.setItem(`catalogueCategory.${slug}.selectedFilterIds`, JSON.stringify(selectedFilterIds || [])); } catch { }
  }, [slug, selectedFilterIds]);

  useEffect(() => {
    let cancelled = false;

    async function loadSuppliers() {
      try {
        const list = await fetchSuppliersPublic();
        if (!cancelled) {
          setSuppliers(list || []);
        }
      } catch (e) {
        // on ignore discrètement l'erreur côté page catégorie
      }
    }

    loadSuppliers();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 700);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (skipScrollRef.current) return;
    if (productsTopRef.current) {
      const offset = 140;
      const elementPosition = productsTopRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  }, [slug, selectedFilterIds, sortMode]);

  const titleProducts = selected ? selected.name : 'Produits de cette catégorie';
  const tree = buildCategoryTree(categories);
  const rootCategories = tree;

  return (
    <div className="catalogue-page-signature">
      <div className="container-catalogue-premium">
        
        {/* HEADER SECTION */}
        <header className="catalogue-header-main">
           <div className="tag-line-premium">FAMILLE DE PRODUITS AEF32</div>
           <h1>{selected ? selected.name : 'Catalogue'}</h1>
           <div className="breadcrumb-signature">
              <span onClick={() => navigate('/catalogue')}>Catalogue</span>
              <span className="sep">/</span>
              <span className="active">{selected ? selected.name : 'Chargement...'}</span>
           </div>
        </header>

        {/* FEATURED CATEGORY CARD */}
        {selected && (
          <section className="featured-category-wrap">
            <div className="category-hero-card shadow-signature animated-up">
              <div className="cat-hero-visual">
                {selected.image ? (
                  <img src={getFileUrl(selected, selected.image)} alt={selected.name} />
                ) : (
                  <div className="cat-placeholder">📂</div>
                )}
              </div>
              <div className="cat-hero-content">
                <div className="cat-accent-pill">SÉLECTION ELITE</div>
                <h2>{selected.name}</h2>
                <div className="cat-description">
                   {selected.description || "Découvrez notre sélection de solutions fiables et esthétiques pour cette gamme, conçue pour s'intégrer durablement à vos projets."}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SUB-CATEGORIES GRID (ROOTS AT THIS LEVEL) */}
        {selected && subCategories.length > 0 && (
          <section className="gammes-section-signature">
            <div className="section-title-box centered-titles">
               <div className="accent-bar-horiz"></div>
               <h2>Sous-catégories</h2>
            </div>
            <div className="gammes-grid-signature">
              {subCategories.filter(it => it.parent === selected.id).map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className="gamme-card-royale shadow-signature"
                  onClick={() => {
                    if (!cat.slug) return;
                    navigate(`/catalogue/${encodeURIComponent(selected.slug)}/${encodeURIComponent(cat.slug)}`);
                  }}
                >
                  <div className="gamme-visual">
                    {cat.image ? (
                      <img src={getFileUrl(cat, cat.image)} alt={cat.name} />
                    ) : (
                      <div className="placeholder-gamme">📂</div>
                    )}
                  </div>
                  <div className="gamme-footer">
                     <span>{cat.name}</span>
                     <div className="arrow-gamme">→</div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* MAIN CATALOGUE AREA */}
        <section ref={productsTopRef} className="main-catalog-area">
          <div className="catalog-toolbar-signature shadow-signature">
            <div className="toolbar-left">
              <h2>Showroom {selected ? selected.name : ''}</h2>
              <div className="status-pill-signature">
                 <span className="dot-live"></span> {productsCount} références trouvées
              </div>
            </div>
            
            <div className="toolbar-right">
              <div className="tool-select-group">
                <span className="tool-label">Trier par</span>
                <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                  <option value="random">Aléatoire</option>
                  <option value="relevance">Pertinence</option>
                  <option value="name-asc">Nom (A → Z)</option>
                  <option value="name-desc">Nom (Z → A)</option>
                </select>
              </div>

              <div className="tool-select-group">
                <span className="tool-label">Naviguer</span>
                <select 
                  value={selectedRootId} 
                  onChange={(e) => {
                    const val = e.target.value;
                    if(!val) return;
                    const find = (nodes, id) => {
                      for(const n of nodes){
                        if(n.id===id) return n;
                        if(n.children?.length){ const r=find(n.children,id); if(r) return r; }
                      }
                      return null;
                    };
                    const node = find(rootCategories, val);
                    if(node && node.slug) navigate(`/catalogue/${node.slug}`);
                  }}
                >
                   <option value="">Changer de gamme</option>
                   {categories.filter(c=>!c.parent).map(c=>(
                     <option key={c.id} value={c.id}>{c.name}</option>
                   ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mobile-filter-trigger-wrap">
            <button className="btn-mobile-filter-gold" onClick={() => setIsMobileFilterOpen(true)}>
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9v6l4 3v-9L22 3z"/></svg>
               Filtrer la gamme
            </button>
          </div>

          <div className="catalog-studio-layout">
            <aside className="catalog-sidebar-signature shadow-signature">
              <div className="sidebar-header-pro">
                 <h3>Filtres Expert</h3>
                 <button className="btn-reset-sidebar" onClick={() => setSelectedFilterIds([selected.id, ...collectDescendantIds(selected)])}>
                    Reset
                 </button>
              </div>
              <div className="sidebar-scroll-area">
                {familyRoot && (
                  <FilterNode
                    node={familyRoot}
                    level={0}
                    selectedIds={selectedFilterIds}
                    expandedIds={expandedIds}
                    onToggle={(ids) => {
                      setSelectedFilterIds(prev => {
                        const allSelected = ids.every(id => prev.includes(id));
                        if (allSelected) return prev.filter(x => !ids.includes(x));
                        const next = [...prev];
                        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
                        return next;
                      });
                      setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
                    }}
                    onToggleExpand={(id) => {
                      setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                    }}
                  />
                )}
              </div>
            </aside>

            <main className="catalog-main-content">
              <ProductList
                categoryId={null}
                categoryIds={selectedFilterIds}
                onCountChange={setProductsCount}
                sortMode={sortMode}
                buildProductUrl={(product) => `/produits/${encodeURIComponent(product.slug)}`}
              />
            </main>
          </div>
        </section>

        {/* MOBILE OVERLAY */}
        {isMobileFilterOpen && (
          <div className="mobile-filter-overlay-signature">
            <div className="overlay-backdrop-blur" onClick={() => setIsMobileFilterOpen(false)}></div>
            <div className="mobile-filter-sheet animated-slide-up">
              <div className="sheet-header">
                <div>
                  <h4>Affiner {selected?.name}</h4>
                  <p>Sélectionnez vos critères</p>
                </div>
                <button className="btn-close-sheet" onClick={() => setIsMobileFilterOpen(false)}>×</button>
              </div>
              <div className="sheet-scroll-area">
                {familyRoot && (
                  <FilterNode
                    node={familyRoot}
                    level={0}
                    selectedIds={selectedFilterIds}
                    expandedIds={expandedIds}
                    onToggle={(ids) => {
                      setSelectedFilterIds(prev => {
                        const allSelected = ids.every(id => prev.includes(id));
                        if (allSelected) return prev.filter(x => !ids.includes(x));
                        const next = [...prev];
                        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
                        return next;
                      });
                      setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
                    }}
                    onToggleExpand={(id) => {
                      setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                    }}
                  />
                )}
              </div>
              <div className="sheet-footer">
                 <button className="btn-apply-sheet" onClick={() => setIsMobileFilterOpen(false)}>
                    Appliquer le filtre ({productsCount})
                 </button>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER SUPPLIERS */}
        {!!suppliers.length && (
          <section className="catalog-suppliers-band">
             <div className="container-about-premium">
                <h2 className="title-signature-mini">Nos fournisseurs officiels</h2>
                <div className="suppliers-pills-row">
                   {suppliers.map(s => (
                     <div key={s.id} className="supplier-pill shadow-signature">
                        {s.logo ? <img src={getFileUrl(s, s.logo)} alt={s.name} /> : <span>{s.name}</span>}
                     </div>
                   ))}
                </div>
             </div>
          </section>
        )}

        <style>{`
          .catalogue-page-signature { background: #fff; padding: 40px 0; min-height: 100vh; font-family: 'Inter', sans-serif; color: #0f172a; }
          .container-catalogue-premium { max-width: 1200px; margin: 0 auto; padding: 0 20px; }

          .catalogue-header-main { text-align: center; margin-bottom: 50px; }
          .tag-line-premium { font-size: 11px; font-weight: 800; color: var(--accent); letter-spacing: 2.5px; margin-bottom: 15px; text-transform: uppercase; }
          .catalogue-header-main h1 { font-size: clamp(28px, 5vw, 42px); font-weight: 900; margin: 0 0 15px 0; letter-spacing: -1.5px; }
          
          .breadcrumb-signature { display: flex; justify-content: center; align-items: center; gap: 10px; font-size: 13px; font-weight: 700; color: #94a3b8; }
          .breadcrumb-signature span:first-child { cursor: pointer; color: #64748b; }
          .breadcrumb-signature span:first-child:hover { color: var(--accent); }
          .breadcrumb-signature .active { color: var(--accent); }
          .breadcrumb-signature .sep { opacity: 0.5; }

          /* FEATURED CARD */
          .featured-category-wrap { margin-bottom: 60px; }
          .category-hero-card { 
             background: #fff; border-radius: 32px; border: 1px solid #f1f5f9; 
             display: grid; grid-template-columns: 400px 1fr; overflow: hidden;
          }
          .cat-hero-visual { height: 280px; background: #f8fafc; border-right: 1px solid #f1f5f9; }
          .cat-hero-visual img { width: 100%; height: 100%; object-fit: cover; }
          .cat-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 60px; opacity: 0.3; }
          
          .cat-hero-content { padding: 40px 60px; display: flex; flex-direction: column; justify-content: center; }
          .cat-accent-pill { 
             width: fit-content; background: #f0fdf4; color: var(--accent); font-size: 10px; 
             font-weight: 900; padding: 6px 15px; border-radius: 99px; margin-bottom: 15px;
             border: 1px solid rgba(34,197,94,0.1);
          }
          .cat-hero-content h2 { font-size: 28px; font-weight: 900; margin: 0 0 15px 0; color: #0f172a; }
          .cat-description { font-size: 16px; color: #64748b; line-height: 1.7; max-width: 600px; }

          /* GAMMES */
          .gammes-section-signature { margin-bottom: 60px; }
          .centered-titles { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 30px; }
          .accent-bar-horiz { width: 40px; height: 3px; background: var(--accent); border-radius: 4px; }
          .gammes-grid-signature { display: flex; flex-wrap: wrap; justify-content: center; gap: 20px; }
          
          .gamme-card-royale { 
             flex: 0 1 210px; background: #fff; border-radius: 20px; border: 1px solid #f1f5f9;
             padding: 10px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
             display: flex; flex-direction: column;
          }
          .gamme-card-royale:hover { transform: translateY(-5px); border-color: var(--accent); box-shadow: 0 20px 40px rgba(34, 197, 94, 0.08); }
          .gamme-visual { height: 130px; border-radius: 12px; overflow: hidden; position: relative; background: #f8fafc; }
          .gamme-visual img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
          .gamme-card-royale:hover .gamme-visual img { transform: scale(1.1); }
          .gamme-footer { padding: 12px 5px 5px 5px; display: flex; justify-content: space-between; align-items: center; font-size: 14px; font-weight: 800; color: #0f172a; }
          .arrow-gamme { color: var(--accent); opacity: 0; transform: translateX(-10px); transition: all 0.3s; }
          .gamme-card-royale:hover .arrow-gamme { opacity: 1; transform: translateX(0); }

          /* TOOLBAR */
          .catalog-toolbar-signature { 
             background: #fff; border-radius: 24px; border: 1px solid #f1f5f9; padding: 25px 35px;
             display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;
          }
          .toolbar-left h2 { font-size: 18px; font-weight: 900; margin: 0 0 5px 0; }
          .status-pill-signature { display: flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
          .dot-live { width: 7px; height: 7px; background: #10b981; border-radius: 50%; box-shadow: 0 0 10px #10b981; }
          
          .toolbar-right { display: flex; gap: 20px; }
          .tool-select-group { display: flex; align-items: center; gap: 10px; }
          .tool-label { font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; }
          .tool-select-group select { border: 1px solid #e2e8f0; border-radius: 10px; padding: 6px 12px; font-size: 12px; font-weight: 700; color: #0f172a; outline: none; background: #fff; cursor: pointer; transition: border-color 0.2s; }
          .tool-select-group select:hover { border-color: var(--accent); }

          /* STUDIO LAYOUT */
          .catalog-studio-layout { display: grid; grid-template-columns: 320px 1fr; gap: 40px; align-items: flex-start; }
          .catalog-sidebar-signature { position: sticky; top: 140px; background: #fff; border-radius: 28px; border: 1px solid #f1f5f9; padding: 30px; overflow: hidden; max-height: calc(100vh - 180px); display: flex; flex-direction: column; }
          .sidebar-header-pro { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
          .sidebar-header-pro h3 { font-size: 15px; font-weight: 950; text-transform: uppercase; letter-spacing: 1px; margin: 0; color: var(--accent); }
          .btn-reset-sidebar { background: #f8fafc; border: none; padding: 5px 12px; border-radius: 8px; font-size: 11px; font-weight: 800; color: #64748b; cursor: pointer; }
          .btn-reset-sidebar:hover { background: #fee2e2; color: #ef4444; }
          .sidebar-scroll-area { flex-grow: 1; overflow-y: auto; padding-right: 10px; }

          .mobile-filter-trigger-wrap { display: none; margin-bottom: 20px; }
          .btn-mobile-filter-emerald { width: 100%; background: var(--accent); color: #fff; border: none; padding: 15px; border-radius: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; gap: 10px; box-shadow: 0 10px 20px rgba(34, 197, 94, 0.2); }

          .mobile-filter-overlay-signature { position: fixed; inset: 0; z-index: 1000; }
          .overlay-backdrop-blur { position: absolute; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(10px); }
          .mobile-filter-sheet { position: absolute; bottom: 0; left: 0; right: 0; background: #fff; border-radius: 32px 32px 0 0; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
          .animated-slide-up { animation: slideUp 0.4s cubic-bezier(0.165, 0.84, 0.44, 1); }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .sheet-header { padding: 25px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
          .sheet-header h4 { font-size: 18px; font-weight: 900; margin: 0; }
          .btn-close-sheet { width: 36px; height: 36px; border-radius: 50%; background: #f1f5f9; border: none; font-size: 20px; cursor: pointer; }
          .sheet-scroll-area { flex-grow: 1; overflow-y: auto; padding: 20px; }
          .sheet-footer { padding: 20px; border-top: 1px solid #f1f5f9; }
          .btn-apply-sheet { width: 100%; background: #0f172a; color: #fff; border: none; padding: 16px; border-radius: 14px; font-weight: 800; }

          .catalog-suppliers-band { padding: 60px 0; border-top: 1px solid #f1f5f9; margin-top: 60px; }
          .title-signature-mini { text-align: center; font-size: 16px; font-weight: 900; color: #0f172a; margin-bottom: 25px; letter-spacing: -0.5px; }
          .suppliers-pills-row { display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; align-items: center; }
          .supplier-pill { background: #fff; border-radius: 50px; border: 1px solid #f1f5f9; padding: 8px 18px; display: flex; align-items: center; justify-content: center; height: 50px; }
          .supplier-pill img { max-height: 30px; max-width: 130px; object-fit: contain; }

          .animated-up { animation: fadeUp 0.6s cubic-bezier(0.165, 0.84, 0.44, 1); }
          @keyframes fadeUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

          @media (max-width: 1024px) {
             .catalog-studio-layout { grid-template-columns: 1fr; }
             .catalog-sidebar-signature { display: none; }
             .mobile-filter-trigger-wrap { display: block; }
             .category-hero-card { grid-template-columns: 1fr; }
             .cat-hero-visual { height: 200px; border-right: none; border-bottom: 1px solid #f1f5f9; }
             .cat-hero-content { padding: 30px; }
          }
        `}</style>
      </div>
    </div>
  );
}

function collectDescendantIds(node) {
  const result = [];
  const stack = [...(node.children || [])];
  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;
    result.push(current.id);
    if (current.children && current.children.length) {
      stack.push(...current.children);
    }
  }
  return result;
}

function FilterNode({ node, level, selectedIds, expandedIds, onToggle, onToggleExpand }) {
  const hasChildren = (node.children || []).length > 0;
  const isSelected = selectedIds.includes(node.id);
  const isExpanded = hasChildren && expandedIds.includes(node.id);

  return (
    <div className={`filter-node-wrap level-${level} ${isSelected ? 'is-selected' : ''}`}>
      <div className="filter-row-main">
        {hasChildren ? (
          <button
            type="button"
            className={`btn-expand-node ${isExpanded ? 'open' : ''}`}
            onClick={() => onToggleExpand(node.id)}
          >
            {isExpanded ? '−' : '+'}
          </button>
        ) : (
          <span className="node-spacer-dot"></span>
        )}
        
        <div 
          className={`filter-clickable-area ${isSelected ? 'active' : ''}`}
          onClick={() => onToggle([node.id, ...collectDescendantIds(node)])}
        >
          <div className="custom-check-box">
             {isSelected && <div className="check-inner"></div>}
          </div>
          <span className="node-name">{node.name}</span>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="node-children-container">
          {node.children.map(child => (
            <FilterNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}

      <style>{`
        .filter-node-wrap { margin-bottom: 4px; }
        .filter-row-main { display: flex; align-items: center; gap: 8px; }
        
        .btn-expand-node { 
           width: 22px; height: 22px; border: 1px solid #e2e8f0; border-radius: 6px; 
           background: #fff; font-size: 14px; color: #64748b; cursor: pointer;
           display: flex; align-items: center; justify-content: center; transition: all 0.2s;
        }
        .btn-expand-node:hover { border-color: var(--accent); color: var(--accent); }
        .btn-expand-node.open { background: #f8fafc; }
        
        .node-spacer-dot { width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; position: relative; }
        .node-spacer-dot::after { content: ''; width: 4px; height: 4px; background: #e2e8f0; border-radius: 50%; }

        .filter-clickable-area { 
           display: flex; align-items: center; gap: 10px; cursor: pointer; flex-grow: 1; 
           padding: 6px 12px; border-radius: 10px; transition: all 0.2s;
        }
        .filter-clickable-area:hover { background: #f8fafc; }
        .filter-clickable-area.active { background: #f0fdf4; }
        
        .custom-check-box { 
           width: 16px; height: 16px; border: 2px solid #e2e8f0; border-radius: 5px; 
           display: flex; align-items: center; justify-content: center; transition: all 0.2s;
        }
        .filter-clickable-area.active .custom-check-box { border-color: var(--accent); background: var(--accent); }
        .check-inner { width: 6px; height: 6px; background: #fff; border-radius: 1px; }

        .node-name { font-size: 14px; font-weight: 600; color: #475569; transition: color 0.2s; }
        .filter-clickable-area.active .node-name { color: var(--accent); font-weight: 800; }
        .filter-clickable-area:hover .node-name { color: #0f172a; }

        .node-children-container { 
           margin-left: 11px; padding-left: 15px; border-left: 1px solid #f1f5f9; 
           margin-top: 5px; margin-bottom: 10px;
        }
        
        .level-0 > .filter-row-main .node-name { font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; font-size: 13px; }
      `}</style>
    </div>
  );
}
